import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";

/** "prospect" -> "Prospect". Local so the modal does not depend on the String prototype extension. */
const capitalize = (value?: string) =>
	value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "";

interface UnflagCompetitorModalProps {
	/** How many companies the action will affect — 1 for a row action. */
	count: number;
	/** Named in the copy when a single company is being un-flagged. */
	companyName?: string;
	isLoading: boolean;
	onClose: () => void;
	onConfirm: (scoreCompanyCategoryId: number) => void;
}

/**
 * Confirms "this is not a competitor" and collects the category the company moves
 * to. The consequences are spelled out because they reach past this table: the
 * company becomes contactable again everywhere, and its leads come back into
 * their lists.
 */
export const UnflagCompetitorModal: FunctionComponent<UnflagCompetitorModalProps> = ({
	count,
	companyName,
	isLoading,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();
	const { data: score } = useGetScoreQuery();
	const [selectedCategory, setSelectedCategory] = useState<{ id: number; title: string } | null>(
		null
	);

	// Competitor and Disqualified are deliberately absent: un-flagging into either
	// would leave the company excluded while reporting that it had been cleared.
	const categories = useMemo(
		() =>
			[...(score?.prospectCompanyCategories ?? []), ...(score?.partnerCompanyCategories ?? [])]
				.filter((category) => !!category.id)
				.map((category) => ({
					id: category.id as number,
					title: `${category.category} (${capitalize(category.type)})`,
				})),
		[score]
	);

	return (
		<Modal
			disableSave={isLoading || !selectedCategory}
			handleClose={onClose}
			handleSave={() => selectedCategory && onConfirm(selectedCategory.id)}
			isLoading={isLoading}
			isOpened
			submitButtonText={t("contacts.competitors.unflagConfirm")}
			title={t("contacts.competitors.unflagTitle")}
		>
			<div className="flex flex-col gap-4">
				<p className="text-sm text-text">
					{companyName && count === 1
						? t("contacts.competitors.unflagIntroOne", { name: companyName })
						: t("contacts.competitors.unflagIntroMany", { count })}
				</p>

				<ul className="list-disc pl-5 text-sm text-text-meta flex flex-col gap-1">
					<li>{t("contacts.competitors.unflagEffectBlacklist")}</li>
					<li>{t("contacts.competitors.unflagEffectLeads")}</li>
					<li>{t("contacts.competitors.unflagEffectFuture")}</li>
				</ul>

				<Dropdown
					data={categories}
					defaultValue={selectedCategory ?? undefined}
					handleSelect={(selected) =>
						setSelectedCategory({ id: selected.id as number, title: selected.title })
					}
					label={t("contacts.competitors.unflagCategoryLabel")}
					portal
					testId="unflag-competitor-category"
				/>

				{categories.length === 0 && (
					<p className="text-xs text-status-error">
						{t("contacts.competitors.unflagNoCategories")}
					</p>
				)}
			</div>
		</Modal>
	);
};
